import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";

interface SignatureDetails {
  document_id: number;
  document_title: string;
  signed_at: string;
  signature_type: string | null;
  signature_mode: string | null;
  signature_reference: string | null;
  signature_ip: string | null;
  signature_user_agent: string | null;
  signature_image_url: string | null;
  signer: { id: number; first_name: string; last_name: string; email: string } | null;
}

export default function SignatureDetailsModal({
  documentId,
  onClose,
}: {
  documentId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [details, setDetails] = useState<SignatureDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<SignatureDetails>(
          `/api/documents/${documentId}/signature-details`,
        );
        setDetails(data);
      } catch (err) {
        setError((err as Error).message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [documentId]);

  const fmtType = (v: string | null) => {
    if (!v) return t("documents.legacySignature", "Legacy signature");
    return { drawn: t("documents.signDraw", "Drawn"), typed: t("documents.signType", "Typed"), uploaded: t("documents.signUpload", "Uploaded") }[v] ?? v;
  };

  const fmtMode = (v: string | null) => {
    if (!v) return t("documents.legacySignature", "Legacy signature");
    return { confirm_receipt: t("documents.modeConfirmReceipt", "Confirm Receipt"), on_document: t("documents.modeOnDocument", "Sign on Document") }[v] ?? v;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          {t("documents.signatureDetails", "Signature Details")}
        </h2>

        {loading && <p className="text-sm text-gray-400">{t("common.loading", "Loading...")}</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {details && (
          <div className="space-y-3">
            {/* Signature image */}
            <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4">
              {details.signature_image_url ? (
                <img
                  src={details.signature_image_url}
                  alt={t("documents.signatureImage", "Signature")}
                  className="mx-auto max-h-40 object-contain"
                />
              ) : (
                <p className="text-center text-sm text-gray-400">
                  {t("documents.signatureImageNotAvailable", "Signature image not available")}
                </p>
              )}
            </div>

            <div className="rounded-lg bg-green-50 p-4">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold text-green-800">
                  {t("documents.documentSigned", "Document Signed")}
                </span>
              </div>
            </div>

            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                <DetailRow label={t("documents.document", "Document")} value={details.document_title} />
                <DetailRow
                  label={t("documents.signedBy", "Signed By")}
                  value={details.signer ? `${details.signer.first_name} ${details.signer.last_name}` : "—"}
                />
                <DetailRow
                  label={t("documents.signerEmail", "Email")}
                  value={details.signer?.email ?? "—"}
                />
                <DetailRow
                  label={t("documents.signedAt", "Signed At")}
                  value={new Date(details.signed_at).toLocaleString()}
                />
                <DetailRow
                  label={t("documents.signatureMethod", "Method")}
                  value={fmtType(details.signature_type)}
                />
                <DetailRow
                  label={t("documents.signatureMode", "Mode")}
                  value={fmtMode(details.signature_mode)}
                />
                <DetailRow
                  label={t("documents.signatureRef", "Reference")}
                  value={details.signature_reference ?? "—"}
                  mono
                />
                <DetailRow
                  label={t("documents.signerIp", "IP Address")}
                  value={details.signature_ip ?? "—"}
                  mono
                />
                <DetailRow
                  label={t("documents.signerAgent", "User Agent")}
                  value={details.signature_user_agent ?? "—"}
                  truncate
                />
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            {t("common.close", "Close")}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
  truncate,
}: {
  label: string;
  value: string;
  mono?: boolean;
  truncate?: boolean;
}) {
  return (
    <tr>
      <td className="whitespace-nowrap py-2 pr-4 text-gray-500">{label}</td>
      <td
        className={[
          "py-2 text-gray-900",
          mono ? "font-mono text-xs" : "",
          truncate ? "max-w-[200px] truncate" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        title={truncate ? value : undefined}
      >
        {value}
      </td>
    </tr>
  );
}
